import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { ShoppingBag, ArrowRight } from "lucide-react";
import confetti from "canvas-confetti";
import { useStore } from "@/store/StoreContext";
import { imgSrc } from "@/lib/api";
import { toast } from "sonner";
import Stars from "./Stars";

const CRACKER_COLORS = ["#D4AF37", "#F6E27A", "#E67300", "#B34A00", "#FDF8F0", "#C0392B"];

function productUrlSlug(product) {
  const nameSlug = (product.name || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
  const savedSlug = product.slug || "";
  // Keep a stored suffix only when it is the 6-character uniqueness suffix
  // generated for a duplicate product name; ignore stale slugs after renaming.
  if (nameSlug && (savedSlug === nameSlug || new RegExp("^" + nameSlug + "-[a-f0-9]{6}$").test(savedSlug))) {
    return savedSlug;
  }
  return nameSlug || savedSlug || product.id;
}

function fireworks(x, y) {
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
  const origin = { x, y };
  confetti({ particleCount: 90, spread: 80, startVelocity: 42, origin, colors: CRACKER_COLORS, ticks: 180, scalar: 1.05, disableForReducedMotion: true });
  setTimeout(() => confetti({ particleCount: 55, spread: 110, startVelocity: 32, origin: { x: Math.min(Math.max(x + 0.08, 0.1), 0.9), y: Math.max(y - 0.12, 0.08) }, colors: CRACKER_COLORS, ticks: 160, scalar: 0.9, disableForReducedMotion: true }), 180);
  setTimeout(() => confetti({ particleCount: 55, spread: 110, startVelocity: 32, origin: { x: Math.min(Math.max(x - 0.08, 0.1), 0.9), y: Math.max(y - 0.1, 0.08) }, colors: CRACKER_COLORS, ticks: 160, scalar: 0.9, disableForReducedMotion: true }), 340);
}

export default function ProductCard({ product }) {
  const { addToCart, setCartOpen, priceOf, originalOf, money } = useStore();
  const ref = useRef(null);

  const price = priceOf(product);
  const original = originalOf(product);
  const discount = original ? Math.round((1 - price / original) * 100) : 0;

  const onMove = (e) => {
    const el = ref.current;
    if (!el || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const r = el.getBoundingClientRect();
    const rx = ((e.clientY - r.top) / r.height - 0.5) * -7;
    const ry = ((e.clientX - r.left) / r.width - 0.5) * 9;
    el.style.transform = `perspective(900px) rotateX(${rx}deg) rotateY(${ry}deg) translateY(-4px)`;
  };
  const onLeave = () => {
    if (ref.current) ref.current.style.transform = "perspective(900px) rotateX(0deg) rotateY(0deg)";
  };

  const handleAdd = (e) => {
    fireworks(e.clientX / window.innerWidth, e.clientY / window.innerHeight);
    const ok = addToCart(product);
    if (ok) {
      toast.success(`${product.name} added to cart`);
      setCartOpen(true);
    } else {
      toast.error("Cart limit reached — maximum 10 different products per order");
    }
  };

  return (
    <article
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      data-testid={`product-card-${product.slug || product.id}`}
      className="card-3d relative flex h-full flex-col overflow-hidden rounded-md border border-[#E9DFC8] bg-white"
    >
      {discount > 0 && (
        <span data-testid={`discount-badge-${product.slug || product.id}`} className="absolute left-4 top-4 z-10 rounded-full bg-[#B34A00] px-3 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-white">
          {discount}% Off
        </span>
      )}
      {product.tag && (
        <span className="absolute right-4 top-4 z-10 rounded-full border border-[#B08D2B]/40 bg-white/85 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-[#8F6E1D] backdrop-blur">
          {product.tag}
        </span>
      )}
      <Link to={`/product/${productUrlSlug(product)}`} data-testid={`product-link-${product.slug || product.id}`} className="relative block aspect-[4/3] w-full overflow-hidden bg-[#F7F0DF]">
        <img
          src={imgSrc(product.image)}
          alt={product.name}
          loading="lazy"
          className="h-full w-full object-contain p-5 transition-transform duration-500 ease-out hover:scale-[1.05]"
        />
      </Link>
      <div className="flex flex-1 flex-col gap-2 border-t border-[#E9DFC8] p-5">
        <h3 className="font-display text-xl font-semibold leading-snug text-[#2B1E12]">{product.name}</h3>
        <Stars slug={product.slug || product.id} />
        <p className="line-clamp-2 text-sm leading-relaxed text-[#6B5B45]">{product.description}</p>
        <div className="mt-auto pt-2">
          <div className="flex items-baseline gap-2.5">
            <span data-testid={`product-price-${product.slug || product.id}`} className="font-display text-[26px] font-bold leading-none text-[#8F6E1D]">
              {money(price)}
            </span>
            {original && (
              <span data-testid={`product-original-${product.slug || product.id}`} className="text-[15px] font-medium text-[#6B5B45]/75 line-through">
                {money(original)}
              </span>
            )}
          </div>
          <div className="mt-3 flex items-center gap-2">
            <button
              data-testid={`add-to-cart-${product.slug || product.id}`}
              onClick={handleAdd}
              className="flex flex-1 items-center justify-center gap-2 bg-[#D4AF37] px-4 py-3 text-[11px] font-bold uppercase tracking-[0.16em] text-[#1C130B] transition-colors duration-300 hover:bg-[#8F6E1D] hover:text-white"
            >
              <ShoppingBag className="h-3.5 w-3.5" /> Add to Cart
            </button>
            <Link
              data-testid={`more-details-${product.slug || product.id}`}
              to={`/product/${productUrlSlug(product)}`}
              aria-label={`More details about ${product.name}`}
              className="flex h-[42px] w-[42px] shrink-0 items-center justify-center rounded-full border border-[#B08D2B]/45 text-[#8F6E1D] transition-all duration-300 hover:bg-[#D4AF37] hover:text-[#1C130B]"
            >
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </div>
    </article>
  );
}
